import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { saveBlob, blobErrorMessage } from '../../utils/downloadBlob';
import { ArrowRight, Download } from 'lucide-react';
import {
  Button,
  DataTable,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  EmptyState,
  AlertMessage,
  PageHeader,
  SearchFilterBar,
  FilterTabs,
} from '../common/ui';
import Pagination from '../common/Pagination';
import { useAccountingActionBar } from './AccountingActionBar';
import { formatDateTime } from '../../utils/dateUtils';
import type { Payment, PaginationInfo, StatsBucket, StatsInfo, StatusFilter, LinkedStatus, VascoLinkStatus, PaymentsResponse, PaymentGkStatus, GkStatusMeta } from './types';
import { gkLastRunFailed } from './PaymentGkBox';

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all',        label: 'All' },
  { key: 'incomplete', label: 'Incomplete' },
  { key: 'force_closed', label: 'Completed' },
  { key: 'linked',     label: 'Linked' },
  { key: 'not linked', label: 'Not linked' },
  { key: 'expected',   label: 'Expected' },
  { key: 'overpaid',   label: 'Overpaid' },
  { key: 'underpaid',  label: 'Underpaid' },
];

const linkedStatusLabel: Record<LinkedStatus, string> = {
  'not linked': 'Not linked',
  auto: 'Linked',
  manual: 'Linked',
  overpaid: 'Overpaid',
  underpaid: 'Underpaid',
  expected: 'Expected',
  duplicate: 'Duplicate',
  partial: 'Partial',
  force_closed: 'Completed',
  vasco_closed: 'Completed',
  item_force_closed: 'Completed',
};

const toIsoDate = (d: Date): string => {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
};

// Default export range: the current calendar month to date.
const defaultExportFrom = (): string => {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
};
const defaultExportTo = (): string => toIsoDate(new Date());

const EXPORT_CURRENCIES = ['EUR', 'USD'] as const;
type ExportCurrency = (typeof EXPORT_CURRENCIES)[number];

const linkedStatusClass: Record<LinkedStatus, string> = {
  'not linked': 'text-red-600 bg-red-50',
  auto: 'text-green-700 bg-green-50',
  manual: 'text-green-700 bg-green-50',
  overpaid: 'text-orange-700 bg-orange-50',
  underpaid: 'text-yellow-700 bg-yellow-50',
  expected: 'text-indigo-700 bg-indigo-50',
  duplicate: 'text-amber-700 bg-amber-50',
  partial: 'text-blue-700 bg-blue-50',
  force_closed: 'text-teal-700 bg-teal-50',
  vasco_closed: 'text-teal-700 bg-teal-50',
  item_force_closed: 'text-teal-700 bg-teal-50',
};

const vascoStatusLabel: Record<VascoLinkStatus, string> = {
  linked: 'Linked',
  partial: 'Partially linked',
  'not linked': 'Not linked',
};

const vascoStatusClass: Record<VascoLinkStatus, string> = {
  linked: 'text-green-700 bg-green-50',
  partial: 'text-blue-700 bg-blue-50',
  'not linked': 'text-red-600 bg-red-50',
};

// While a GK recalculation is pending, the list asks this often whether it finished.
const GK_STATUS_POLL_MS = 15000;
// After a failed calculation the job waits 5 minutes before it tries again.
const GK_STATUS_POLL_AFTER_FAILURE_MS = 60000;

/** Hover text of the GK column. */
function gkTitle(gk: PaymentGkStatus): string {
  if (gk.state === 'none') return 'Not part of the GK export (before the start date, other currency, or a pending settlement).';
  if (gk.state === 'unknown') {
    return gk.reason === 'export_cancelled'
      ? 'The GK export of this payment was cancelled. The status is being calculated again.'
      : 'Not calculated yet.';
  }
  if (gk.state === 'exported') return `Exported to GK in document ${gk.dokument}.`;
  const lines = gk.exceptions.map((e) => `${e.code}: ${e.message}`);
  if (gk.state === 'ready') lines.push('No problem with this payment.');
  if (gk.dayHeld) {
    const by = gk.heldBy.length > 0 ? ` Other payments with problems: ${gk.heldBy.join(', ')}.` : '';
    lines.push(`Day ${gk.day} is held (${gk.dayCodes.join(', ')}), so it is not exported yet.${by}`);
  }
  lines.push(`Calculated: ${formatDateTime(gk.computedAt)}.`);
  return lines.join('\n');
}

/** The GK column: Ready in green, the payment's own B codes in red (as in the GK preview). */
const GkCell: React.FC<{ gk: PaymentGkStatus | null | undefined }> = ({ gk }) => {
  if (!gk) return <span className="text-xs text-gray-400">—</span>;
  const title = gkTitle(gk);
  if (gk.state === 'none') return <span className="text-xs text-gray-400" title={title}>—</span>;
  if (gk.state === 'unknown') return <span className="text-xs text-gray-500" title={title}>Unknown</span>;
  if (gk.state === 'exported') return <span className="text-xs font-medium text-gray-700" title={title}>Exported</span>;
  if (gk.state === 'blocked') return <span className="text-xs font-medium text-red-700" title={title}>{gk.codes.join(', ')}</span>;
  return <span className="text-xs font-medium text-green-700" title={title}>Ready</span>;
};

const Accounting: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, limit: 50, total: 0 });
  const [stats, setStats] = useState<StatsInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [gkStatus, setGkStatus] = useState<GkStatusMeta | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    (searchParams.get('status') as StatusFilter) || 'all'
  );
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  // Only the newest request writes the page, so a slow older response (for
  // example a reload after a GK recalculation) never replaces a newer page.
  const requestSeq = useRef(0);
  // silent: keep the current rows visible while loading (a background reload).
  const fetchPayments = useCallback(async (page: number, search: string, status: StatusFilter, signal?: AbortSignal, { silent = false } = {}) => {
    const seq = ++requestSeq.current;
    const isCurrent = () => !signal?.aborted && seq === requestSeq.current;
    if (!silent) setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: '50' };
      if (search) params.search = search;
      if (status && status !== 'all') params.status = status;

      const { data } = await axios.get<PaymentsResponse>('/api/accounting/payments', { params, signal });
      if (!isCurrent()) return;
      setFetchError('');
      setPayments(data.payments);
      setPagination(data.pagination);
      setStats(data.stats);
      setGkStatus(data.gkStatus ?? null);
    } catch (err: any) {
      if (axios.isCancel(err) || err.name === 'CanceledError' || !isCurrent()) return;
      // A failed background reload keeps the rows that are on the page.
      if (!silent) setFetchError(err.response?.data?.error || 'Failed to load payments');
    } finally {
      if (seq === requestSeq.current && !signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchPayments(currentPage, searchTerm, statusFilter, controller.signal);
    return () => controller.abort();
  }, [fetchPayments, currentPage, searchTerm, statusFilter]);

  const refetchCurrentPage = () => fetchPayments(currentPage, searchTerm, statusFilter);

  // While the GK status is being recalculated, ask whether it finished. When
  // a new calculation is there, load the page again in the background; when
  // only the job state changed (for example it failed), update the header.
  const gkPending = gkStatus?.pending === true;
  const gkComputedAt = gkStatus?.computedAt ?? null;
  const gkFailed = gkLastRunFailed(gkStatus);
  useEffect(() => {
    if (!gkPending) return undefined;
    const controller = new AbortController();
    let busy = false;
    const timer = setInterval(async () => {
      if (busy) return;
      busy = true;
      try {
        const { data } = await axios.get<GkStatusMeta>('/api/accounting/gk-export/payment-status', { signal: controller.signal });
        if (controller.signal.aborted) return;
        if (!data.pending || data.computedAt !== gkComputedAt) {
          await fetchPayments(currentPage, searchTerm, statusFilter, controller.signal, { silent: true });
        } else {
          setGkStatus(data);
        }
      } catch {
        // The next tick tries again.
      } finally {
        busy = false;
      }
    }, gkFailed ? GK_STATUS_POLL_AFTER_FAILURE_MS : GK_STATUS_POLL_MS);
    return () => {
      clearInterval(timer);
      controller.abort();
    };
  }, [gkPending, gkComputedAt, gkFailed, fetchPayments, currentPage, searchTerm, statusFilter]);

  const { actionButtons, actionAlerts } = useAccountingActionBar(refetchCurrentPage);

  const updateUrlParams = (status: StatusFilter, search: string, page: number) => {
    const params = new URLSearchParams();
    if (status && status !== 'all') params.set('status', status);
    if (search && search.trim()) params.set('search', search.trim());
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
  };

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    setSearchTerm(trimmed);
    setCurrentPage(1);
    updateUrlParams(statusFilter, trimmed, 1);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
    updateUrlParams(statusFilter, '', 1);
  };

  const handleStatusFilterChange = (filter: StatusFilter) => {
    setStatusFilter(filter);
    setCurrentPage(1);
    updateUrlParams(filter, searchTerm, 1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrlParams(statusFilter, searchTerm, page);
  };

  // Sync state from URL on browser back/forward
  useEffect(() => {
    const statusFromUrl = (searchParams.get('status') as StatusFilter) || 'all';
    const searchFromUrl = searchParams.get('search') || '';
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    if (statusFromUrl !== statusFilter) setStatusFilter(statusFromUrl);
    if (searchFromUrl !== searchTerm) {
      setSearchInput(searchFromUrl);
      setSearchTerm(searchFromUrl);
    }
    if (pageFromUrl !== currentPage) setCurrentPage(pageFromUrl);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Reconciliation export -------------------------------------------------
  const [exportFrom, setExportFrom] = useState(defaultExportFrom);
  const [exportTo, setExportTo] = useState(defaultExportTo);
  const [exporting, setExporting] = useState<ExportCurrency | null>(null);
  const [exportError, setExportError] = useState('');

  const exportRangeInvalid = !exportFrom || !exportTo || exportFrom > exportTo;

  const handleExport = async (currency: ExportCurrency) => {
    if (exportRangeInvalid) return;
    setExporting(currency);
    setExportError('');
    try {
      const res = await axios.get('/api/accounting/payments/export', {
        params: { currency, from: exportFrom, to: exportTo },
        responseType: 'blob',
      });
      saveBlob(res.data, `payments-reconciliation-${currency}-${exportFrom}_${exportTo}.xlsx`);
    } catch (err: any) {
      // The error body arrives as a Blob because responseType is 'blob'.
      setExportError(await blobErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(null);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title="Accounting"
        subtitle="Reconcile Woo orders with Revolut payments"
        actions={actionButtons}
      />

      {actionAlerts}
      {fetchError && (
        <AlertMessage type="error" message={fetchError} title="" onDismiss={() => setFetchError('')} dismissible>
          {''}
        </AlertMessage>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <Button
          variant="secondary"
          onClick={() => navigate('/accounting/b2b-orders')}
          icon={ArrowRight}
        >
          Group by B2B order
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate('/accounting/vasco-invoices')}
          icon={ArrowRight}
        >
          Group by Vasco invoice
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate('/accounting/gk-export')}
          icon={ArrowRight}
        >
          Vasco GK export
        </Button>
      </div>

      {exportError && (
        <AlertMessage type="error" message={exportError} title="" onDismiss={() => setExportError('')} dismissible>
          {''}
        </AlertMessage>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
        <div className="flex flex-col">
          <label htmlFor="export-from" className="mb-1 text-xs font-medium text-gray-500">
            From
          </label>
          <input
            id="export-from"
            type="date"
            value={exportFrom}
            max={exportTo || undefined}
            onChange={(e) => setExportFrom(e.target.value)}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="export-to" className="mb-1 text-xs font-medium text-gray-500">
            To
          </label>
          <input
            id="export-to"
            type="date"
            value={exportTo}
            min={exportFrom || undefined}
            onChange={(e) => setExportTo(e.target.value)}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {EXPORT_CURRENCIES.map((currency) => (
          <Button
            key={currency}
            variant="secondary"
            icon={Download}
            onClick={() => handleExport(currency)}
            disabled={exportRangeInvalid || exporting !== null}
          >
            {exporting === currency ? `Exporting ${currency}…` : `Export ${currency}`}
          </Button>
        ))}
        {exportRangeInvalid && (
          <span className="self-center text-xs text-red-600">
            Select a valid date range (from ≤ to).
          </span>
        )}
      </div>

      <SearchFilterBar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        onSearchSubmit={handleSearch}
        onSearch={handleSearch}
        onSearchClear={handleSearchClear}
        searchPlaceholder="Search by counterparty, description, or order number..."
        onFilterChange={() => {}}
      />

      <FilterTabs
        filters={STATUS_FILTERS}
        activeFilter={statusFilter}
        onFilterChange={handleStatusFilterChange}
      />

      {gkPending && (
        <p className="mb-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="gk-list-updating">
          The GK status is being calculated again after a change. Until it is done, the GK column
          {gkStatus?.decidesTabs ? ' and the tabs Incomplete, Linked and Completed' : ''} can be out of date.
          {gkFailed
            ? ' The last calculation failed; the next try is in a few minutes.'
            : ' The list updates by itself, in about half a minute.'}
        </p>
      )}

      <DataTable emptyState={null}>
        <TableHeader>
          <tr>
            <TableHeaderCell responsive={undefined}>Date & Time</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Counterparty</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Source</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Amount</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Orders</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Vasco</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>
              <span title="Status in the Vasco GK export (glavna knjiga)">GK</span>
              {gkStatus?.error ? (
                <span className="ml-1 normal-case font-normal text-red-700" title={`The last GK calculation failed: ${gkStatus.error}`}>(failed)</span>
              ) : gkPending ? (
                <span className="ml-1 normal-case font-normal text-gray-500" title="Data changed after the last calculation. The column updates by itself.">(updating…)</span>
              ) : null}
            </TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow onClick={undefined} href={undefined} onKeyDown={undefined}>
              <TableCell responsive={undefined} colSpan={7} className="p-0">
                <EmptyState
                  message="Loading payments…"
                  action={false}
                  actionText=""
                  onActionClick={() => {}}
                />
              </TableCell>
            </TableRow>
          ) : fetchError ? (
            <TableRow onClick={undefined} href={undefined} onKeyDown={undefined}>
              <TableCell responsive={undefined} colSpan={7} className="p-0">
                <EmptyState
                  message="Unable to load payments."
                  action={false}
                  actionText=""
                  onActionClick={() => {}}
                />
              </TableCell>
            </TableRow>
          ) : payments.length === 0 ? (
            <TableRow onClick={undefined} href={undefined} onKeyDown={undefined}>
              <TableCell responsive={undefined} colSpan={7} className="p-0">
                <EmptyState
                  message={
                    searchTerm
                      ? `No payments match "${searchTerm}".`
                      : 'No payments. Use "Sync" to import payments.'
                  }
                  action={false}
                  actionText=""
                  onActionClick={() => {}}
                />
              </TableCell>
            </TableRow>
          ) : (
            payments.map((payment) => (
              <TableRow
                key={payment.id}
                clickable={true}
                onClick={() => navigate(`/accounting/payments/${payment.id}`)}
                href={`/accounting/payments/${payment.id}`}
                onKeyDown={undefined}
              >
                <TableCell responsive={undefined}>{formatDateTime(payment.date)}</TableCell>
                <TableCell responsive={undefined}>
                  <span className="text-sm block max-w-[160px] truncate" title={payment.counterparty || undefined}>
                    {payment.counterparty || '—'}
                  </span>
                </TableCell>
                <TableCell responsive={undefined}>
                  {payment.paymentSource ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                      {payment.paymentSource}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell responsive={undefined}>
                  <span className="font-medium tabular-nums text-green-700">
                    {payment.currency} {payment.amount.toFixed(2)}
                  </span>
                </TableCell>
                <TableCell responsive={undefined}>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${linkedStatusClass[payment.linkedStatus]}`}
                  >
                    {linkedStatusLabel[payment.linkedStatus]}
                  </span>
                </TableCell>
                <TableCell responsive={undefined}>
                  <div className="space-y-1">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${vascoStatusClass[payment.vascoStatus]}`}
                    >
                      {vascoStatusLabel[payment.vascoStatus]}
                    </span>
                    {/* Directly claimed Vasco invoices (SW-2486) */}
                    {(payment.linkedVascoNumbers?.length ?? 0) > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {(payment.linkedVascoNumbers ?? []).map((n) => (
                          <span
                            key={n}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                          >
                            Vasco {n}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell responsive={undefined}>
                  <GkCell gk={payment.gk} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </DataTable>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        onPageChange={handlePageChange}
        onItemsPerPageChange={() => {}}
        showItemsPerPageSelector={false}
      />

      {/* Stats footer */}
      {stats && (
        <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
          {([
            ['Current page', stats.page],
            ['Last 30 days', stats.last30Days],
            ['Last year', stats.lastYear],
          ] as [string, StatsBucket][]).map(([label, s]) => (
            <div key={label} className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-400 font-medium mb-1.5">{label}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-600 font-medium">{s.total}</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-green-700 bg-green-50">
                  {s.linked} linked
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-red-600 bg-red-50">
                  {s.notLinked} not linked
                </span>
                {s.overpaid > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-orange-700 bg-orange-50">
                    {s.overpaid} overpaid
                  </span>
                )}
                {s.underpaid > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-yellow-700 bg-yellow-50">
                    {s.underpaid} underpaid
                  </span>
                )}
                {s.expected > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-indigo-700 bg-indigo-50">
                    {s.expected} expected
                  </span>
                )}
                {(s.forceClosed ?? 0) > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-teal-700 bg-teal-50">
                    {s.forceClosed} completed
                  </span>
                )}
                {(s.gkBlocked ?? 0) > 0 && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-red-700 bg-red-50"
                    title="Linked or completed, but the payment has a GK problem, so it is in Incomplete."
                  >
                    {s.gkBlocked} GK problems
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Accounting;
